/**
 * NOS-226: Background-import status card for the dashboard. Imports run fully
 * server-side (NOS-178), so users can finish onboarding and leave while stage 2
 * runs — this card is where a returning user sees their import's state:
 *  - running (queued/crawling/mapping): live phase + determinate progress
 *  - review: paused at the preview gate — deep links to the builder's review
 *    panel (NOS-192/NOS-223 flow) and surfaces the 7-day expiry
 *  - failed: the job's user-facing error + "Try again" (re-runs the same URL)
 *  - done: dismissible success with links to the live site + import report
 * Dismissal persists per job id (localStorage) so a NEW job re-surfaces the
 * card. Polling only — reuses useImportJob's cadence, no realtime.
 */
import { useCallback, useState } from "react"
import { Link } from "react-router-dom"
import { AlertCircle, CheckCircle2, Loader2, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  contentSummaryFromJob,
  protectedPagesFromJob,
  IMPORT_REVIEW_TTL_MS,
  useImportJob,
  useLatestImportJob,
  type ImportJobRow,
} from "@/hooks/useImportJob"
import { useOrgStore } from "@/stores/orgStore"

const DISMISS_KEY_PREFIX = "nos-import-card-dismissed:"

/** Terminal states the user can dismiss; live states always show. */
const DISMISSIBLE_STATUSES = ["done", "failed"]
const RUNNING_STATUSES = ["queued", "crawling", "mapping"]

export function isImportCardDismissed(jobId: string): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY_PREFIX + jobId) === "1"
  } catch {
    return false
  }
}

export function dismissImportCard(jobId: string): void {
  try {
    localStorage.setItem(DISMISS_KEY_PREFIX + jobId, "1")
  } catch {
    // localStorage unavailable — dismissal just won't persist
  }
}

/** Whole days left before a review-paused job expires (0 = expired). */
export function reviewDaysLeft(
  job: Pick<ImportJobRow, "updated_at">,
  now: number = Date.now()
): number {
  const t = Date.parse(job.updated_at ?? "")
  if (!Number.isFinite(t)) return 0
  const msLeft = t + IMPORT_REVIEW_TTL_MS - now
  return Math.max(0, Math.ceil(msLeft / (24 * 60 * 60 * 1000)))
}

export function ImportStatusCard({ orgId, orgSlug }: { orgId: string; orgSlug: string }) {
  const initiateCrawl = useOrgStore((s) => s.initiateCrawl)
  const latest = useLatestImportJob(orgId)
  // A retry starts a NEW job — poll it directly (useLatestImportJob only
  // resolves the latest job id once on mount).
  const [retryJobId, setRetryJobId] = useState<string | null>(null)
  const retried = useImportJob(retryJobId)
  const job = retryJobId ? retried.job : latest.job

  const [retryBusy, setRetryBusy] = useState(false)
  const [retryError, setRetryError] = useState<string | null>(null)
  // Local echo of localStorage so dismissing re-renders immediately.
  const [dismissedIds, setDismissedIds] = useState<string[]>([])

  const handleDismiss = useCallback(() => {
    if (!job) return
    const id = job.id
    dismissImportCard(id)
    setDismissedIds((ids) => (ids.includes(id) ? ids : [...ids, id]))
  }, [job])

  const handleRetry = useCallback(async () => {
    if (!job) return
    setRetryBusy(true)
    setRetryError(null)
    const result = await initiateCrawl(job.source_url, orgId)
    setRetryBusy(false)
    if (!result.ok || !result.jobId) {
      setRetryError(result.error ?? "Import did not start. Please try again.")
      return
    }
    setRetryJobId(result.jobId)
  }, [job, orgId, initiateCrawl])

  if (!job) return null
  if (
    DISMISSIBLE_STATUSES.includes(job.status) &&
    (dismissedIds.includes(job.id) || isImportCardDismissed(job.id))
  ) {
    return null
  }

  if (RUNNING_STATUSES.includes(job.status)) {
    const pct =
      job.pages_found > 0
        ? Math.min(100, Math.round((job.pages_processed / job.pages_found) * 100))
        : null
    return (
      <section className="mb-8" data-testid="import-status-card">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-base">Importing your site</CardTitle>
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden />
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-sm text-muted-foreground">{job.phase ?? "Working on it…"}</p>
            {pct != null && (
              <>
                <div
                  className="h-2 w-full overflow-hidden rounded-full bg-muted"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={pct}
                  aria-label="Import progress"
                >
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {job.pages_processed} of {job.pages_found} pages
                </p>
              </>
            )}
            <p className="text-xs text-muted-foreground">
              This runs in the background — you can keep working or come back later.
            </p>
          </CardContent>
        </Card>
      </section>
    )
  }

  if (job.status === "review") {
    const daysLeft = reviewDaysLeft(job)
    return (
      <section className="mb-8" data-testid="import-status-card">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Your import preview is ready</CardTitle>
            <CardDescription>
              We imported a sample of your site and paused for your review. Approve it to import
              the rest.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              {daysLeft > 0
                ? `This preview expires in ${daysLeft} day${daysLeft !== 1 ? "s" : ""} — after that, only the sample pages are kept.`
                : "This preview has expired — only the sample pages will be kept."}
            </p>
          </CardContent>
          <CardFooter>
            <Button size="sm" asChild>
              <Link to="/dashboard/builder">Review import</Link>
            </Button>
          </CardFooter>
        </Card>
      </section>
    )
  }

  if (job.status === "failed") {
    return (
      <section className="mb-8" data-testid="import-status-card">
        <Card className="border-destructive/50">
          <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 shrink-0 text-destructive" aria-hidden />
              <CardTitle className="text-base">Import failed</CardTitle>
            </div>
            <button
              type="button"
              onClick={handleDismiss}
              aria-label="Dismiss"
              className="rounded-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </CardHeader>
          <CardContent className="space-y-1">
            <p className="text-sm text-destructive">
              {job.error ?? "Something went wrong during the import."}
            </p>
            <p className="text-xs text-muted-foreground">From {job.source_url}</p>
            {retryError && (
              <p className="text-xs text-destructive" role="alert">
                {retryError}
              </p>
            )}
          </CardContent>
          <CardFooter>
            <Button size="sm" variant="outline" onClick={handleRetry} disabled={retryBusy}>
              {retryBusy ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  Restarting…
                </>
              ) : (
                "Try again"
              )}
            </Button>
          </CardFooter>
        </Card>
      </section>
    )
  }

  if (job.status === "done") {
    const summary = contentSummaryFromJob(job)
    const protectedPages = protectedPagesFromJob(job)
    return (
      <section className="mb-8" data-testid="import-status-card">
        <Card>
          <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" aria-hidden />
              <CardTitle className="text-base">Site import complete</CardTitle>
            </div>
            <button
              type="button"
              onClick={handleDismiss}
              aria-label="Dismiss"
              className="rounded-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </CardHeader>
          <CardContent className="space-y-1">
            <p className="text-sm text-muted-foreground">
              {summary
                ? `${summary.pages} page${summary.pages !== 1 ? "s" : ""} and ${summary.posts} post${summary.posts !== 1 ? "s" : ""} imported.`
                : "Your site content has been imported."}
            </p>
            {/* NOS-253: late-entry recovery — protected drafts can still be unlocked */}
            {protectedPages.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {protectedPages.length} page{protectedPages.length !== 1 ? "s were" : " was"}{" "}
                password-protected and imported as {protectedPages.length !== 1 ? "drafts" : "a draft"}.
                Know the password? You can still import the real content.
              </p>
            )}
          </CardContent>
          <CardFooter className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" asChild>
              <a href={`/${orgSlug}`} target="_blank" rel="noopener noreferrer">
                View site
              </a>
            </Button>
            <Button size="sm" variant="outline" asChild>
              <Link to="/dashboard/builder">View import report</Link>
            </Button>
            {protectedPages.length > 0 && (
              <Button size="sm" asChild>
                <Link to="/dashboard/builder?recover=protected">Unlock protected pages</Link>
              </Button>
            )}
          </CardFooter>
        </Card>
      </section>
    )
  }

  return null
}
